import { app, auth } from "@/firebaseConfig";

/**
 * How many songs a page of the repertoire holds. The stored cursors are cut
 * to it, so everything that pages the songs has to use this one.
 */
export const SONGS_PAGE_SIZE = 15;

/** Where a page ends: the sort value and id of its last document. */
export type PageCursor = { id: string; sortValue: string | number };

type RestRow = {
  document?: {
    name: string;
    fields?: Record<
      string,
      { stringValue?: string; integerValue?: string; doubleValue?: number }
    >;
  };
};

/**
 * The last document of every page of an organisation's `collectionName`,
 * sorted by `sortField` and then id, and how many documents that is.
 *
 * Read over the Firestore REST API because the web SDK can only fetch whole
 * documents, and of a song this needs one field: its chapters are most of
 * its size. A document without `sortField` is left out by the ordering,
 * which is what keeps a song's variants out of the pages.
 */
export async function buildPageCursors(
  collectionName: string,
  sortField: string,
  account: number,
  pageSize: number,
): Promise<{ lastDocs: PageCursor[]; totalItems: number }> {
  const idToken = await auth.currentUser?.getIdToken();
  const response = await fetch(
    `https://firestore.googleapis.com/v1/projects/${app.options.projectId}/databases/(default)/documents:runQuery`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({
        structuredQuery: {
          select: { fields: [{ fieldPath: sortField }] },
          from: [{ collectionId: collectionName }],
          where: {
            fieldFilter: {
              field: { fieldPath: "account" },
              op: "EQUAL",
              value: { integerValue: String(account) },
            },
          },
          orderBy: [
            { field: { fieldPath: sortField }, direction: "ASCENDING" },
            { field: { fieldPath: "__name__" }, direction: "ASCENDING" },
          ],
        },
      }),
    },
  );
  if (!response.ok) {
    throw new Error(`Listing ${collectionName} failed (${response.status})`);
  }

  // An empty result is still one row, holding only the read time.
  const rows = ((await response.json()) as RestRow[]).flatMap((row) => {
    if (!row.document) return [];
    const value = row.document.fields?.[sortField];
    return [
      {
        id: row.document.name.split("/").pop() ?? "",
        sortValue:
          value?.stringValue ??
          Number(value?.integerValue ?? value?.doubleValue),
      },
    ];
  });

  const lastDocs: PageCursor[] = [];
  for (let i = 0; i < rows.length; i += pageSize) {
    // Cursor for page N must be the last item from page N.
    lastDocs.push(rows[Math.min(i + pageSize - 1, rows.length - 1)]);
  }
  return { lastDocs, totalItems: rows.length };
}
